import {FC, useMemo} from "react";
import {matchPath, useLocation} from "react-router-dom";
import {styled} from "@mui/material/styles";
import {useAppSelector} from "../../../hooks/redux";
import {useIsAdmin} from "../../../hooks/useIsAdmin";
import {selectCompanyTariffName} from "../../../features/companies/model/selectors";
import {menuConfig, MenuGroupConfig, MenuItemConfig} from "../../../config/menuConfig";
import MenuItem from "./MenuItem";
import MenuCollapseGroup from "./MenuCollapseGroup";
import MenuGroup from "./MenuGroup";

/**
 * ============================================================================
 * MenuContent — рендеринг бокового меню по конфигурации.
 * ============================================================================
 *
 * Компонент делает следующее:
 *   1. Фильтрует menuConfig по правам (admin, tariff).
 *   2. Разделяет группы на две зоны:
 *      — основные (top zone): rendered в обычном потоке
 *      — pinned to bottom (bottom zone): прижаты к низу через flex
 *   3. Определяет активный пункт через matchPath по location.pathname.
 *   4. Рендерит группы (обычные и сворачиваемые) и пункты внутри.
 *
 * Архитектура layout'а:
 *   MenuContent занимает всю высоту скроллящейся зоны drawer'a.
 *   Внутри — flex column с justify-content: space-between.
 *
 *     ┌──────────────────────┐
 *     │ top zone             │  ← основные группы (operations/fleet/admin)
 *     │ (естественная высота)│
 *     │                      │
 *     ├──────────────────────┤
 *     │                      │
 *     │     (пустота)        │  ← flex auto, растягивается
 *     │                      │
 *     ├──────────────────────┤
 *     │ bottom zone          │  ← pinToBottom-группы (Справочники)
 *     │ (естественная высота)│
 *     └──────────────────────┘
 *
 * Если содержимого больше, чем высота → скролл работает естественно
 * через .scroll-area родителя.
 *
 * Разделение зон — через ПРОСТРАНСТВО (flex-распор), а не через рамку.
 * Линию-divider над bottom-зоной убрали намеренно: по дизайн-системе
 * структура задаётся пространством и типографикой, рамки — только там,
 * где семантически обязательны. Заголовок-caption группы («СПРАВОЧНИКИ»)
 * сам по себе читается как уровень, а пустой распор сверху уже отделяет
 * её от основных групп. Линия лишь добавляла визуальный шум и дублировала
 * это разделение.
 * ============================================================================
 */

interface MenuContentProps {
    /** Вызывается при клике на пункт (для закрытия mobile drawer'а). */
    onItemClick?: () => void;
}

const Root = styled("nav")({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "100%",
    paddingTop: 8,
    paddingBottom: 8,
});

const Zone = styled("div")({
    display: "flex",
    flexDirection: "column",
});

const MenuContent: FC<MenuContentProps> = ({onItemClick}) => {
    const {pathname} = useLocation();
    const isAdmin = useIsAdmin();
    const companyTariffName = useAppSelector(selectCompanyTariffName);

    // Фильтрация конфига по правам.
    const visibleGroups = useMemo<MenuGroupConfig[]>(() => {
        return menuConfig
            .filter((group) => {
                if (group.requiresAdmin && !isAdmin) return false;
                if (group.requiresTariff && group.requiresTariff !== companyTariffName) return false;
                return true;
            })
            .map((group) => ({
                ...group,
                items: group.items.filter((item) => {
                    if (item.requiresAdmin && !isAdmin) return false;
                    if (item.requiresTariff && item.requiresTariff !== companyTariffName) return false;
                    return true;
                }),
            }))
            .filter((group) => group.items.length > 0);
    }, [isAdmin, companyTariffName]);

    const topGroups = visibleGroups.filter((g) => !g.pinToBottom);
    const bottomGroups = visibleGroups.filter((g) => g.pinToBottom);

    const isItemActive = (item: MenuItemConfig): boolean => {
        return !!matchPath({path: item.path, end: false}, pathname);
    };

    const renderItem = (item: MenuItemConfig, indent = 0) => (
        <div key={item.path} onClick={onItemClick}>
            <MenuItem
                to={item.path}
                label={item.label}
                icon={item.icon}
                iconActive={item.iconActive}
                isActive={isItemActive(item)}
                indent={indent}
            />
        </div>
    );

    const renderGroup = (group: MenuGroupConfig) => {
        if (group.collapsible) {
            // Если внутри есть активный пункт — раскрываем принудительно.
            const hasActiveChild = group.items.some(isItemActive);

            // Иконку collapsible-группы берём из конфига. Если её там нет —
            // это конфигурационная ошибка, в dev-режиме видно через console.
            if (!group.icon) {
                if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `[MenuContent] Collapsible group "${group.id}" has no icon in menuConfig.`
                    );
                }
            }

            return (
                <MenuCollapseGroup
                    key={group.id}
                    id={group.id}
                    title={group.title ?? ""}
                    icon={group.icon}
                    iconActive={group.iconActive}
                    defaultOpen={group.defaultOpen}
                    forceOpen={hasActiveChild}
                >
                    {group.items.map((item) => renderItem(item, 1))}
                </MenuCollapseGroup>
            );
        }

        return (
            <MenuGroup key={group.id} title={group.title}>
                {group.items.map((item) => renderItem(item))}
            </MenuGroup>
        );
    };

    return (
        <Root aria-label="Главное меню">
            <Zone>
                {topGroups.map(renderGroup)}
            </Zone>

            {bottomGroups.length > 0 && (
                <Zone>
                    {bottomGroups.map(renderGroup)}
                </Zone>
            )}
        </Root>
    );
};

export default MenuContent;